import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useToken } from '../auth/useToken';
import { streamAsk } from '../lib/sse';
import { chatReducer, initialChatState } from '../state/chatStore';
import { loadVerbosity } from '../state/verbosity';
import { Sidebar } from './Sidebar';
import { EmptyState } from './EmptyState';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { Notice } from './Notice';
import { SettingsPopover } from './SettingsPopover';
import { DeleteAccountDialog } from './DeleteAccountDialog';
import { ChronosBanner } from './ChronosBanner';
import { ChronosToast } from './ChronosToast';
import { getConversation, listConversations, type ConversationSummary } from '../lib/conversations';
import { listPrompts, type SavedPrompt } from '../lib/prompts';

const SIDEBAR_WIDTH_STORAGE_KEY = 'kairos.sidebar.width';

function loadSidebarWidth(): number {
  const raw = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
  const n = parseInt(raw ?? '', 10);
  return Number.isFinite(n) && n >= 220 && n <= 480 ? n : 268;
}

function saveSidebarWidth(px: number) {
  localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(px));
}

/**
 * Compute a sticky title for the in-progress thread before the server-side
 * GenerateConversationTitleCommand has populated Conversation.Title.
 * Falls back to the first user message truncated to ~60 chars.
 */
function titleFor(messages: { role: string; content?: string }[]): string {
  const firstUser = messages.find(m => m.role === 'user');
  const raw = firstUser?.content ?? '';
  const text = raw.trim();
  if (!text) return 'New chat';
  return text.length > 60 ? text.slice(0, 57) + '…' : text;
}

export function ChatScreen() {
  const getToken = useToken();
  const { instance } = useMsal();

  const [state, dispatch] = useReducer(chatReducer, initialChatState);

  // Lifted state shared with the Sidebar + EmptyState.
  const [prompts, setPrompts] = useState<SavedPrompt[]>([]);
  const [_recentConversations, setRecentConversations] = useState<ConversationSummary[]>([]);
  // Bumped after every /ask turn completes so the Sidebar refetches its
  // conversations list — newly-created threads + async-generated titles
  // need to appear without a page reload.
  const [sidebarRefreshTick, setSidebarRefreshTick] = useState(0);

  // UI state for the new shell.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [deleteMeOpen, setDeleteMeOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => loadSidebarWidth());
  const [prefill, setPrefill] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Prime the lifted prompts list once on mount so both Sidebar and
  // EmptyState render from the same source of truth.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listPrompts(await getToken());
        if (!cancelled) setPrompts(list);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [getToken]);

  // After each finished turn, refresh the sidebar's conversations list. The
  // server persists a brand-new thread just after the `done` event, titled
  // with a fallback (the truncated first question) that
  // GenerateConversationTitleCommand then replaces with the generated title.
  // That swap usually lands within ~0.5s of the row appearing but the
  // smaller title-gen model occasionally takes longer.
  //
  // A fixed ladder (e.g. [0, 2s, 5s, 10s]) misses the long-tail cases. Poll
  // instead: refetch immediately, then every few seconds until the active
  // thread shows up in the list with a non-empty title — plus one more
  // refetch after that, so a fallback title seen first gets replaced by the
  // generated one — or until we hit a ~30s cap. Stops cleanly on success —
  // no constant background polling.
  useEffect(() => {
    if (state.inFlight) return;
    if (state.messages.length === 0) return;
    let cancelled = false;
    const activeThreadKey = state.threadKey;
    // Delays between successive polls. Total ≈ 2+3+4+5+5+5+5 = 29s.
    const delays = [0, 2000, 3000, 4000, 5000, 5000, 5000, 5000];
    let i = 0;
    let seenTitled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      try {
        const list = await listConversations(await getToken());
        if (cancelled) return;
        setRecentConversations(list);
        setSidebarRefreshTick(n => n + 1);
        // Stop polling once the active thread appears in the list with a
        // title (the row is what the user is waiting to see). If we
        // never created a thread this turn (state.threadKey still null),
        // just one refresh is enough.
        const titled = activeThreadKey
          ? list.some(c => c.threadKey === activeThreadKey && (c.title ?? '').trim().length > 0)
          : true;
        if (titled) {
          // One more refetch after the first sighting so a fallback title
          // is swapped for the generated one; stop on the second.
          if (!activeThreadKey || seenTitled) return;
          seenTitled = true;
        }
      } catch { /* silent — schedule next tick anyway */ }
      i += 1;
      if (i < delays.length) timer = setTimeout(tick, delays[i]);
    };

    timer = setTimeout(tick, delays[0]);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [getToken, state.inFlight, state.messages.length, state.threadKey]);

  const send = useCallback(
    async (query: string, opts?: { forceNew?: boolean }) => {
      // `forceNew` is set by callers that semantically mean "start a brand
      // new conversation" (e.g. clicking a saved prompt while another
      // thread is already active). Without it, send would carry the
      // current state.threadKey forward and append the new question as
      // turn N+1 of the previous conversation — which makes saved-prompt
      // clicks accumulate into the wrong thread.
      if (opts?.forceNew) {
        abortRef.current?.abort();
        dispatch({ type: 'new-conversation' });
      }

      const userMessage = { id: crypto.randomUUID(), role: 'user' as const, content: query };
      dispatch({ type: 'send', userMessage });

      const controller = new AbortController();
      abortRef.current = controller;
      const startTs = performance.now();
      const verbosity = loadVerbosity();

      // When forceNew, never carry an existing threadKey — the server mints
      // a fresh one and we adopt it from the meta event.
      const threadKey = opts?.forceNew ? undefined : (state.threadKey ?? undefined);

      try {
        const token = await getToken();
        await streamAsk({
          token,
          signal: controller.signal,
          body: {
            query,
            threadKey,
            // Whenever we have no thread, explicitly tell the server to mint
            // a new one. Without this the server falls back to the caller's
            // last "active" thread (CurrentThreadCache.Get, 15-min sliding
            // TTL) and silently appends — which makes saved-prompt clicks and
            // the first question after "+ New chat" pile into whatever
            // conversation happened to be open last, even though the SPA UI
            // looks fresh.
            newConversation: threadKey === undefined,
            verbosity,
          },
          onEvent: (e) => {
            if (e.event === 'meta') {
              const data = e.data as {
                threadKey?: string;
                turnNumber?: number;
                turn?: number;
                toolCallStarted?: string;
                retrievedChunks?: { title: string; webViewLink: string; mimeType?: string }[];
              };
              dispatch({
                type: 'meta',
                threadKey: data.threadKey,
                turn: data.turnNumber ?? data.turn,
                toolCallStarted: data.toolCallStarted,
                retrievedChunks: data.retrievedChunks,
              });
            } else if (e.event === 'delta') {
              dispatch({ type: 'delta', text: (e.data as { text: string }).text });
            } else if (e.event === 'done') {
              const durationSeconds = (performance.now() - startTs) / 1000;
              dispatch({ type: 'done', durationSeconds, verbosity });
            } else if (e.event === 'error') {
              const data = e.data as { message?: string };
              dispatch({
                type: 'error',
                notice: { kind: 'error', message: data?.message || 'The response was interrupted. Try again.', onRetry: () => send(query) },
              });
            }
          },
          onError: (err) => {
            if (controller.signal.aborted) return;
            const retry = () => send(query);
            if (err.status === 503 && err.errorCode === 'kairos_paused') {
              dispatch({ type: 'error', notice: { kind: 'paused', message: err.message, onRetry: retry } });
            } else if (err.status === 503) {
              dispatch({ type: 'error', notice: { kind: 'unavailable', message: err.message, onRetry: retry } });
            } else if (err.status === 429) {
              dispatch({ type: 'error', notice: { kind: 'rate-limited', retryAfter: err.retryAfter, message: err.message, onRetry: retry } });
            } else if (err.status === 403) {
              dispatch({ type: 'error', notice: { kind: 'forbidden', message: err.message } });
            } else if (err.status === 401) {
              dispatch({ type: 'error', notice: { kind: 'unauthorized', message: err.message } });
            } else {
              dispatch({ type: 'error', notice: { kind: 'error', message: err.status ? `Error: HTTP ${err.status}` : err.message, onRetry: retry } });
            }
          },
        });
      } catch { /* handled in onError */ }
    },
    [getToken, state.threadKey],
  );

  // Closing the mobile drawer also flips `aria-hidden` on the drawer wrapper
  // to true. If the user just tapped a button INSIDE the drawer (the green
  // plus, a history row, the close-X), focus is still on that button when the
  // ancestor goes hidden — Chrome warns ("Blocked aria-hidden on an element
  // because its descendant retained focus") and screen readers lose track of
  // the cursor. Blur the active element before we set the drawer state.
  const closeDrawer = useCallback(() => {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el !== document.body) el.blur();
    setDrawerOpen(false);
  }, []);

  const onNewChat = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: 'new-conversation' });
    closeDrawer();
    // Focus the composer so the click has visible feedback even when the
    // user was already on a fresh empty state — without this, tapping
    // the green plus on an already-empty pane feels dead because the
    // dispatch is a no-op (state was already initialChatState). Defer
    // past the render so the textarea exists + isn't disabled.
    requestAnimationFrame(() => {
      document.querySelector<HTMLTextAreaElement>('#input-form textarea')?.focus();
    });
  }, [closeDrawer]);

  // When the user deletes a thread from the sidebar, we only need to react
  // here if the deleted thread is what they're currently looking at —
  // otherwise the sidebar already handled its own optimistic removal and
  // the chat pane is showing an unrelated conversation. Comparing inside
  // the callback (rather than at the Sidebar) keeps the active-thread
  // truth in one place: ChatScreen owns the chat-pane state.
  const onConversationDeleted = useCallback((deletedThreadKey: string) => {
    if (state.threadKey === deletedThreadKey) {
      abortRef.current?.abort();
      dispatch({ type: 'new-conversation' });
    }
  }, [state.threadKey]);

  const onResumeSession = useCallback(async (threadKey: string) => {
    abortRef.current?.abort();
    // Close the drawer up front, BEFORE the async fetch. The previous
    // ordering (await getConversation → dispatch resume → closeDrawer)
    // made the drawer linger for the full network round-trip on mobile;
    // users assumed the first tap didn't register and tapped again,
    // double-firing onResumeSession. Closing first is the same pattern
    // onPickPrompt already uses for saved-prompt clicks. The chat-pane
    // briefly shows the previous content (or empty state) until the
    // fetched messages dispatch in.
    closeDrawer();
    try {
      const conv = await getConversation(await getToken(), threadKey);
      dispatch({ type: 'resume', messages: conv.messages, threadKey: conv.threadKey, title: conv.title });
    } catch (err) {
      // Surface so the next time the API can't reach the DB (or the thread
      // 404s) we don't silently lose the click. Previous behaviour was
      // catch-and-ignore, which made it look like the row was simply
      // dead — and routed someone straight to "Where did my prompts go?"
      console.error('Failed to resume conversation', threadKey, err);
    }
  }, [getToken, closeDrawer]);

  // Saved-prompt click sends immediately, and crucially starts a fresh
  // thread — otherwise the question gets appended to whatever conversation
  // happens to be open, producing the "everything piles into one thread"
  // surprise. The forceNew opt tells send() to reset the threadKey before
  // dispatching the new user turn.
  const onPickPrompt = useCallback((text: string) => {
    closeDrawer();
    void send(text, { forceNew: true });
  }, [send, closeDrawer]);

  const onSignOut = useCallback(() => {
    instance.logoutRedirect();
  }, [instance]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: 'abort' });
  }, []);

  // Escape: abort in-flight turn (mirrors the Stop button affordance).
  useEffect(() => {
    if (!state.inFlight) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') abort(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.inFlight, abort]);

  // Mobile drawer: Esc closes; body scroll-lock while open; edge-swipe to
  // open (touch starts within 20 px of the left edge, drags > 50 px right);
  // swipe-left to dismiss when the drawer is open (drag > 80 px left).
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [drawerOpen, closeDrawer]);

  // Edge swipe — only attempt to open when the drawer is closed AND the
  // touch begins near the left edge of the viewport.
  useEffect(() => {
    let startX: number | null = null;
    let startY: number | null = null;
    let triggered = false;
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const fromLeftEdge = !drawerOpen && t.clientX <= 20;
      const fromInsideDrawer = drawerOpen;
      if (!fromLeftEdge && !fromInsideDrawer) { startX = null; return; }
      startX = t.clientX;
      startY = t.clientY;
      triggered = false;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (startX == null || startY == null || triggered) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      // Reject mostly-vertical drags (scroll) before triggering.
      if (dy > Math.abs(dx)) { startX = null; return; }
      if (!drawerOpen && dx > 50) { setDrawerOpen(true); triggered = true; }
      if (drawerOpen && dx < -80) { setDrawerOpen(false); triggered = true; }
    };
    const onTouchEnd = () => { startX = null; startY = null; triggered = false; };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [drawerOpen]);

  // Status dot for the sidebar brand row.
  const sideStatus: 'online' | 'rate-limited' | 'paused' = (() => {
    const lastNotice = state.notice?.kind ?? state.messages[state.messages.length - 1]?.notice?.kind;
    if (lastNotice === 'paused') return 'paused';
    if (lastNotice === 'rate-limited') return 'rate-limited';
    return 'online';
  })();

  // Iter-2 stripped saved prompts out of the Sidebar — it's now history-only.
  // EmptyState still receives the lifted prompts list directly below.
  const sidebarProps = {
    activeThreadKey: state.threadKey,
    onNewChat,
    onResumeSession,
    onOpenSettings: () => setSettingsOpen(true),
    onSignOut,
    status: sideStatus,
    refreshTick: sidebarRefreshTick,
    onConversationDeleted,
  };

  return (
    <div className="shell">
      {/* Desktop sidebar (always rendered; hidden on mobile via CSS .shell-side @media). */}
      <Sidebar
        {...sidebarProps}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed(c => !c)}
        width={sidebarWidth}
        onResize={(px) => { setSidebarWidth(px); saveSidebarWidth(px); }}
      />

      {/* Main pane — header + messages/empty + composer. */}
      <main className="shell-main">
        <ChronosToast />

        <header className="main-head">
          {/* Anchor the title to the same centered 880px column the messages
              and composer live in (composer-width handoff). Without this
              wrapper the title hugs the chat pane's left edge while the
              prose beneath it starts ~hundreds-of-pixels right on a wide
              monitor — they read as detached. */}
          <div className="message-row">
            <div className="main-head-title">
              {/* Mobile-only hamburger — opens the sidebar drawer. Hidden on
                  desktop where the sidebar is always there. Inline SVG (not
                  the <Icon> sprite) per the iOS Safari sprite-quirk memory
                  (feedback_web_icon_sprite_ios_safari). */}
              <button
                type="button"
                className="main-head__hamburger"
                aria-label="Open chats"
                title="Open chats"
                onClick={() => setDrawerOpen(true)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>

              {/* Mobile-only brand badge — Kairos icon + name + online dot.
                  Per the v2-mobile-closed mock the closed-drawer title bar
                  shows the brand here, not a "New chat" title. Desktop
                  hides this and shows the desktop title (icon + label)
                  below — that's where the active conversation's title goes. */}
              <div className="main-head__brand">
                <img src="/kairos-icon.png" alt="" width={28} height={28} style={{ borderRadius: 6 }} />
                <div>
                  <div className="main-head__brand-name">Kairos</div>
                  <div className="status">Online</div>
                </div>
              </div>

              {/* Desktop title — chat-icon mark + "New chat" / active title.
                  Hidden on mobile via .main-head__desktop-title @media. */}
              <span className="main-head-title__icon main-head__desktop-title" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </span>
              <span className="main-head__desktop-title">{state.messages.length === 0 ? 'New chat' : titleFor(state.messages)}</span>

              {/* Mobile-only New-chat green plus on the FAR RIGHT — per
                  handoff-mobile-new-chat, lets the user start a new
                  conversation in one tap without opening the drawer. The
                  sidebar's own .side-new-btn stays in place inside the
                  drawer for users already navigating their history.
                  Hidden on desktop (the persistent sidebar's + is always
                  visible there). Inline SVG per the iOS Safari
                  sprite-quirk memory. */}
              <button
                type="button"
                className="main-head__new"
                aria-label="New chat"
                title="New chat"
                onClick={onNewChat}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        <ChronosBanner />
        {state.notice && (
          <div className="px-4">
            <Notice {...state.notice} />
          </div>
        )}

        {state.messages.length === 0 ? (
          <EmptyState
            prompts={prompts}
            onPromptsChange={setPrompts}
            onPick={onPickPrompt}
          />
        ) : (
          <MessageList messages={state.messages} />
        )}

        <Composer
          onSubmit={send}
          onAbort={abort}
          inFlight={state.inFlight}
          prefill={prefill}
          onPrefillConsumed={() => setPrefill(null)}
        />
      </main>

      {/* Mobile slide-in drawer */}
      <div className={`mobile-drawer${drawerOpen ? ' is-open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="mobile-drawer__scrim" onClick={closeDrawer} />
        <div className="mobile-drawer__panel">
          <Sidebar
            {...sidebarProps}
            mobile
            onClose={closeDrawer}
          />
        </div>
      </div>

      <SettingsPopover
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onOpenDeleteMe={() => { setSettingsOpen(false); setDeleteMeOpen(true); }}
      />
      <DeleteAccountDialog
        open={deleteMeOpen}
        onOpenChange={setDeleteMeOpen}
        prompts={prompts}
        conversations={_recentConversations}
      />
    </div>
  );
}
